import { Subscribable, Subscription } from '@benzinga/subscribable';
import { ScannerRequest, ScannerInstrumentsRequestEvent } from './request';
import { Session } from '@benzinga/session';
import { ScannerProtos } from './scanner_protos';
import { SafePromise } from '@benzinga/safe-await';
import { deepEqual } from '@benzinga/utils';
import { ScannerFeed } from './feed';
import { ScannerConfig } from '@benzinga/scanner-config-manager';
import type { PeerQuote } from '@benzinga/quotes-manager';

import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';
import { toScannerFeedConfig } from './utils';
import {
  getRelatedSymbolsForSymbol,
  getRelatedSymbolsForSymbols,
  RelatedSymbolsForSymbolOptions,
  RelatedSymbolsForSymbolsOptions,
  RelatedSymbol,
  WatchlistRelatedSymbol,
} from './relatedSymbols';
import { getRelatedSymbolPeerQuotes } from './relatedSymbolPeerQuotes';

export type ScannerManagerEvent = ScannerInstrumentsRequestEvent;
export class ScannerManager extends Subscribable<ScannerManagerEvent> {
  private request: ScannerRequest;
  private session: Session;
  private requestSubscription?: Subscription<ScannerRequest>;
  private feeds: { config: ScannerConfig; feed: ScannerFeed }[] = [];

  constructor(session: Session) {
    super();
    this.session = session;
    this.request = new ScannerRequest(session);
    this.session.getManager(QuotesV3FieldsManager).getScannerDefs();
  }

  public static getName = () => 'benzinga-scanner';

  public getInstruments = async (query: ScannerConfig): SafePromise<ScannerProtos.IQueryResponse> => {
    return this.request.getScannerInstruments(query);
  };

  /**
   * Get instruments using the same query format as @benzinga/legacy-scanner-manager (to not break existing code)
   */
  public getInstrumentsWithIQuery = async (
    query: ScannerProtos.IQuery,
    source: 'stocks' | 'crypto' = 'stocks',
  ): SafePromise<ScannerProtos.IQueryResponse> => {
    return this.request.getScannerInstrumentsWithQuery(query, source);
  };

  public getFeed = (config: ScannerConfig) => {
    // Key feeds by query-relevant fields only so GPT tags / metadata do not spawn sockets.
    const feedConfig = toScannerFeedConfig(config);
    const feed = this.feeds.find(f => deepEqual(f.config, feedConfig));
    if (feed) {
      return feed.feed;
    } else {
      const newFeed = {
        config: feedConfig,
        feed: new ScannerFeed(this.session, feedConfig, this.request, () => {
          this.feeds = this.feeds.filter(f => f.feed !== newFeed.feed);
        }),
      };
      this.feeds.push(newFeed);
      return newFeed.feed;
    }
  };

  public getSectorHeatmap = async () => {
    return this.request.getSectorHeatmap();
  };

  public getRelatedSymbols = async (options: RelatedSymbolsForSymbolOptions): Promise<RelatedSymbol[]> => {
    return getRelatedSymbolsForSymbol(this.session, options);
  };

  public getRelatedSymbolsForWatchlist = async (
    options: RelatedSymbolsForSymbolsOptions,
  ): Promise<WatchlistRelatedSymbol[]> => {
    return getRelatedSymbolsForSymbols(this.session, options);
  };

  public getRelatedSymbolPeerQuotes = async (
    options: RelatedSymbolsForSymbolOptions,
  ): Promise<Record<string, PeerQuote>> => {
    return getRelatedSymbolPeerQuotes(this.session, options);
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }
}
